import {
  prisma,
  type Prisma,
  type SeatSectionConfig,
  BookableItemType,
  BookableItemConfigSource,
  EntityType,
  EntityStatus,
  OrderItemStatus,
  SeatSectionStatus,
} from "client";
import { cloneBookableItem } from "../bookableItem";
import {
  ACTIVE_SEAT_SECTION_DELETE_BLOCKED,
  validateSeatSectionChange,
} from "./seat-section-rules";

/**
 * Get all entities
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findMany arguments
 * @returns Array of entities
 */
export async function getAllEntities(
  userId: string,
  payload?: Prisma.EntityFindManyArgs
) {
  try {
    return await prisma.entity.findMany(payload);
  } catch (error: any) {
    throw new Error(`Failed to get entities: ${error.message}`);
  }
}

/**
 * Get entities by event ID
 * @param eventId - Event ID
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findMany arguments
 * @returns Array of entities
 */
export async function getEntitiesByFloorPlanId(
  floorPlanId: string,
  userId: string,
  payload?: Prisma.EntityFindManyArgs
) {
  try {
    return await prisma.entity.findMany({
      where: { floorPlanId },
      ...payload,
    });
  } catch (error: any) {
    throw new Error(`Failed to get entities: ${error.message}`);
  }
}

/**
 * Get entities by exhibitor ID
 * @param exhibitorId - Exhibitor ID
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findMany arguments
 * @returns Array of entities
 */
export async function getEntitiesByExhibitorId(
  exhibitorId: string,
  userId: string,
  payload?: Prisma.EntityFindManyArgs
) {
  try {
    return await prisma.entity.findMany({
      where: { exhibitorId },
      ...payload,
    });
  } catch (error: any) {
    throw new Error(`Failed to get entities: ${error.message}`);
  }
}

/**
 * Get entities by shape ID
 * @param shapeId - Shape ID
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findMany arguments
 * @returns Array of entities
 */
export async function getEntitiesByShapeId(
  shapeId: string,
  userId: string,
  payload?: Prisma.EntityFindManyArgs
) {
  try {
    return await prisma.entity.findMany({
      where: { shapeId },
      ...payload,
    });
  } catch (error: any) {
    throw new Error(`Failed to get entities: ${error.message}`);
  }
}

/**
 * Get an entity by ID
 * @param id - Entity ID
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findUnique arguments
 * @returns Entity or null if not found
 */
export async function getEntityById(
  id: string,
  userId?: string,
  payload?: Prisma.EntityFindUniqueArgs
) {
  try {
    return await prisma.entity.findUnique({
      where: { id },
      ...payload,
    });
  } catch (error: any) {
    throw new Error(`Failed to get entity: ${error.message}`);
  }
}

export const getEntitySummaryByVenueId = async (
  venueId: string,
  floorPlanId?: string,
  type?: EntityType,
  status?: EntityStatus
) => {
  const whereClause: Prisma.EntityWhereInput = {
    venueId,
    ...(floorPlanId && { floorPlanId }),
    ...(type && { type }),
    ...(status && { status }),
  };
  const assignedEntitiesCountPromise = prisma.entity.count({
    where: {
      ...whereClause,
      status: EntityStatus.RESERVED,
      exhibitorId: { not: null },
    },
  });
  const availableEntitiesCountPromise = prisma.entity.count({
    where: {
      ...whereClause,
      status: EntityStatus.AVAILABLE,
    },
  });
  const blockedEntitiesCountPromise = prisma.entity.count({
    where: {
      ...whereClause,
      status: EntityStatus.BLOCKED,
    },
  });
  const onHoldEntitiesCountPromise = prisma.entity.count({
    where: {
      ...whereClause,
      status: EntityStatus.ON_HOLD,
    },
  });
  const totalPromise = prisma.entity.count({
    where: whereClause,
  });

  const [
    assignedEntitiesCount,
    availableEntitiesCount,
    blockedEntitiesCount,
    onHoldEntitiesCount,
    total,
  ] = await Promise.all([
    assignedEntitiesCountPromise,
    availableEntitiesCountPromise,
    blockedEntitiesCountPromise,
    onHoldEntitiesCountPromise,
    totalPromise,
  ]);

  return {
    assignedEntitiesCount,
    availableEntitiesCount,
    blockedEntitiesCount,
    onHoldEntitiesCount,
    total,
  };
};

/**
 * Get an entity by venue ID
 * @param venueId - Venue ID
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findUnique arguments
 * @returns Entity or null if not found
 */
export async function getEntityByVenueId({
  venueId,
  floorPlanId,
  type,
  status,
  orderItemStatus,
  free,
  page = 1,
  pageSize = 50,
  payload,
}: {
  venueId: string;
  floorPlanId?: string;
  type?: EntityType;
  status?: EntityStatus;
  orderItemStatus?: OrderItemStatus;
  free?: boolean;
  page: number;
  pageSize: number;
  payload?: Prisma.EntityFindManyArgs;
}) {
  try {
    const whereClause: Prisma.EntityWhereInput = {
      venueId,
      ...(floorPlanId && { floorPlanId }),
      ...(type && { type }),
      ...(status && { status }),
      ...(orderItemStatus && {
        orderItems: {
          some: {
            status: orderItemStatus,
          },
        },
      }),
      ...(free && {
        entityTier: {
          bookableItem: {
            price: 0,
          },
        },
      }),
      ...(payload?.where ?? {}),
    };
    const entitiesPromise = prisma.entity.findMany({
      where: whereClause,
      include: {
        ...(payload?.include ?? {}),
        exhibitor: {
          include: {
            translations: true,
          },
        },
        bookableItem: {
          include: {
            participantGroups: true,
          },
        },
        entityTier: {
          include: {
            bookableItem: true,
          },
        },
        orderItems: true,
      },
      skip: (page - 1) * pageSize,
      take: pageSize,
      // ...payload,
    });
    const totalPromise = prisma.entity.count({
      where: whereClause,
    });

    const [entities, total] = await Promise.all([
      entitiesPromise,
      totalPromise,
    ]);
    const pagination = {
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize),
      hasNextPage: total > page * pageSize,
    };
    return {
      entities,
      pagination,
    };
  } catch (error: any) {
    throw new Error(`Failed to get entities: ${error.message}`);
  }
}

export const getEntitiesByEventId = async (
  eventId: string,
  payload?: Prisma.EntityFindManyArgs
) => {
  try {
    return await prisma.entity.findMany({
      where: { venue: { eventIds: { has: eventId } } },
      ...payload,
    });
  } catch (error: any) {
    throw new Error(`Failed to get entities: ${error.message}`);
  }
};

/**
 * Validate entity label uniqueness in floor plan
 * @param label - Entity label
 * @param floorPlanId - Floor plan ID
 * @param entityIdToExclude - Entity ID to exclude from validation
 * @returns { isUnique: boolean; message?: string } - Validation result
 */
export async function validateEntityLabelUniquenessInFloorPlan(
  label: string,
  floorPlanId: string,
  entityIdToExclude?: string
): Promise<{ isUnique: boolean; message?: string }> {
  try {
    const normalized = label.trim();
    if (!normalized) return { isUnique: true };

    // Mongo + Prisma: live rows usually omit `deletedAt` entirely. Filtering
    // only `deletedAt: null` misses those and uniqueness always passed.
    // Soft-deleted (`deletedAt` set) must not block reuse.
    const existingEntity = await prisma.entity.findFirst({
      where: {
        label: normalized,
        floorPlanId,
        OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
        ...(entityIdToExclude ? { id: { not: entityIdToExclude } } : {}),
      },
      select: { id: true },
    });

    if (existingEntity) {
      return {
        isUnique: false,
        message: `An entity with label "${normalized}" already exists on this floor plan`,
      };
    }

    return { isUnique: true };
  } catch (error) {
    console.error("Error validating entity label uniqueness:", error);
    return {
      isUnique: false,
      message: "Failed to validate entity label uniqueness",
    };
  }
}

/**
 * Create a new entity
 * @param floorPlanId - Floor plan ID (required)
 * @param payload - Entity data
 * @param userId - User ID for permission checking
 * @param entityTierId - Optional entity tier ID (uses floor plan default if not provided)
 * @returns Created entity
 */

interface EntityRelations {
  venueId: string;
  floorPlanId: string;
  shapeId: string;
  entityTierId: string;
  connectedExhibitorId?: string;
}

export async function createEntity(
  entityRelations: EntityRelations,
  payload: Pick<
    Prisma.EntityCreateInput,
    "label" | "type" | "properties" | "seatSection"
  >,
  userId: string
) {
  try {
    const {
      venueId,
      floorPlanId,
      shapeId,
      entityTierId,
      connectedExhibitorId,
    } = entityRelations;

    const createLabel =
      typeof payload.label === "string" ? payload.label.trim() : "";
    if (createLabel) {
      const validation = await validateEntityLabelUniquenessInFloorPlan(
        createLabel,
        floorPlanId
      );
      if (!validation.isUnique) {
        throw new Error(validation.message);
      }
    }

    return await prisma.$transaction(async (tx) => {
      // Get the tier to access its eventId
      const tier = await tx.entityTier.findUnique({
        where: { id: entityTierId },
        select: {
          id: true,
        },
      });

      if (!tier) {
        throw new Error("EntityTier not found");
      }

      // Create the entity with required relations
      const entity = await tx.entity.create({
        data: {
          ...payload,
          ...(createLabel ? { label: createLabel } : {}),
          floorPlan: {
            connect: { id: floorPlanId },
          },
          venue: {
            connect: { id: venueId },
          },
          shape: {
            connect: { id: shapeId },
          },
          entityTier: {
            connect: { id: entityTierId },
          },
          exhibitor: connectedExhibitorId
            ? {
                connect: { id: connectedExhibitorId },
              }
            : undefined,
          configSource: BookableItemConfigSource.TIER,
        },
        include: {
          bookableItem: true,
          entityTier: {
            include: {
              bookableItem: true,
            },
          },
        },
      });

      // Keep denormalized Shape.entityId in sync (Entity owns shapeId FK)
      await tx.shape.update({
        where: { id: shapeId },
        data: { entityId: entity.id },
      });

      return entity;
    });
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to create entity: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Update an existing entity
 * @param id - Entity ID
 * @param payload - Update data
 * @param userId - User ID for permission checking
 * @returns Updated entity
 */
export async function updateEntity(
  id: string,
  payload: Prisma.EntityUpdateInput,
  userId: string,
  newEntityTierId?: string
) {
  try {
    return await prisma.$transaction(async (tx) => {
      // Get current entity state
      const currentEntity = await tx.entity.findUnique({
        where: { id },
        select: {
          id: true,
          label: true,
          floorPlanId: true,
          entityTierId: true,
          configSource: true,
          shapeId: true,
          seatSection: true,
          type: true,
          entityTier: {
            select: {
              bookableItemId: true,
            },
          },
        },
      });

      if (!currentEntity) {
        throw new Error("Entity not found");
      }

      // Seat identity is immutable once the section is ACTIVE. Shape-type /
      // body-size locks live on the shape write path — not here.
      if (payload.seatSection !== undefined) {
        const issues = validateSeatSectionChange({
          current: currentEntity.seatSection,
          next: payload.seatSection as Partial<SeatSectionConfig>,
        });
        if (issues.length > 0) {
          throw new Error(issues.map((issue) => issue.message).join(" "));
        }
      }

      const nextLabel =
        typeof payload.label === "string" ? payload.label.trim() : undefined;

      // Product lock: ACTIVE section name stays on tickets / composed seat
      // labels. Not identity, but renaming after sales is confusing.
      if (
        currentEntity.type === EntityType.SEATS_SECTION &&
        currentEntity.seatSection?.status === SeatSectionStatus.ACTIVE &&
        nextLabel !== undefined &&
        nextLabel !== currentEntity.label
      ) {
        throw new Error(
          "Cannot rename an active seats section — sold tickets already show this name"
        );
      }

      // Validate label uniqueness if label is being updated
      if (nextLabel !== undefined && nextLabel !== currentEntity.label) {
        const validation = await validateEntityLabelUniquenessInFloorPlan(
          nextLabel,
          currentEntity.floorPlanId,
          id
        );

        if (!validation.isUnique) {
          throw new Error(validation.message);
        }
        payload.label = nextLabel;
      }

      // Handle configSource switching
      if (
        payload.configSource &&
        payload.configSource !== currentEntity.configSource &&
        payload.configSource === BookableItemConfigSource.ENTITY
      ) {
        // Switching from TIER to ENTITY: Clone tier's bookableItem to entity's bookableItem
        const clonedBookableItem = await cloneBookableItem(
          currentEntity.entityTier.bookableItemId,
          BookableItemType.ENTITY
        );

        // Update entity to use the new cloned bookableItem
        await tx.entity.update({
          where: { id },
          data: {
            bookableItem: {
              connect: { id: clonedBookableItem.id },
            },
          },
        });
      }
      // If switching from ENTITY to TIER, the entity's bookableItem is simply not used
      // No need to clear it - it's preserved in case they switch back

      // Update the entity with provided payload
      const updatedEntity = await tx.entity.update({
        where: { id },
        data: payload,
        include: {
          bookableItem: true,
          entityTier: {
            include: {
              bookableItem: true,
            },
          },
        },
      });

      return updatedEntity;
    });
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to update entity: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Delete an entity (soft delete for entity and bookableItem, hard delete for shape)
 * @param id - Entity ID
 * @param userId - User ID for permission checking
 * @returns Deleted entity
 */
export async function deleteEntity(id: string, userId: string) {
  try {
    return await prisma.$transaction(async (tx) => {
      const now = new Date();

      // Get entity with minimal data
      const entity = await tx.entity.findUnique({
        where: { id },
        select: {
          id: true,
          type: true,
          shapeId: true,
          bookableItemId: true,
          deletedAt: true,
          seatSection: true,
        },
      });

      if (!entity) {
        throw new Error("Entity not found");
      }

      if (entity.deletedAt) {
        throw new Error("Entity is already deleted");
      }

      if (
        entity.type === EntityType.SEATS_SECTION &&
        entity.seatSection?.status === SeatSectionStatus.ACTIVE
      ) {
        throw new Error(ACTIVE_SEAT_SECTION_DELETE_BLOCKED);
      }

      // Soft delete entity
      const deletedEntity = await tx.entity.update({
        where: { id },
        data: { deletedAt: now },
      });

      // Soft delete associated bookableItem
      if (entity.bookableItemId) {
        await tx.bookableItem.update({
          where: { id: entity.bookableItemId },
          data: { deletedAt: now },
        });
      }

      // Hard delete shape (no deletedAt field)
      await tx.shape.delete({
        where: { id: entity.shapeId },
      });

      return deletedEntity;
    });
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to delete entity: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Remove an entity but keep its shape (undo entity-create, detach).
 * Soft-deletes bookableItem, clears shape.entityId, hard-deletes the entity row
 * so shapeId unique is free for a later create on the same shape.
 */
export async function detachEntity(id: string, _userId: string) {
  try {
    return await prisma.$transaction(async (tx) => {
      const now = new Date();
      const entity = await tx.entity.findUnique({
        where: { id },
        select: {
          id: true,
          type: true,
          shapeId: true,
          bookableItemId: true,
          deletedAt: true,
          seatSection: true,
        },
      });

      if (!entity) {
        throw new Error("Entity not found");
      }

      if (entity.deletedAt) {
        throw new Error("Entity is already deleted");
      }

      if (
        entity.type === EntityType.SEATS_SECTION &&
        entity.seatSection?.status === SeatSectionStatus.ACTIVE
      ) {
        throw new Error(ACTIVE_SEAT_SECTION_DELETE_BLOCKED);
      }

      if (entity.bookableItemId) {
        await tx.bookableItem.update({
          where: { id: entity.bookableItemId },
          data: { deletedAt: now },
        });
      }

      await tx.shape.update({
        where: { id: entity.shapeId },
        data: { entityId: null },
      });

      await tx.entity.delete({ where: { id } });

      return { id: entity.id, shapeId: entity.shapeId };
    });
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to detach entity: ${error.message}`);
    }
    throw error;
  }
}
